import { css, cx } from "@emotion/css";
import { EmptyStateNoResults, ErrorPanel, DeprecatedIconButton, SearchInput, Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowRefreshIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, InfoCircleIcon } from "@octopusdeploy/design-system-icons";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, fontFamilyGlobal, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { KubernetesEventType } from "@octopusdeploy/octopus-server-client";
import { AllKubernetesEventTypes } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import { Fragment, useMemo, useState } from "react";
import type { LiveStatusObjectDrawerTabProps } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/TabProps";
import { createErrorInfoFromMonitorError } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/createErrorInfoFromMonitorError";
import { useEventsQuery } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/useEventsQuery";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import DateFormatter from "~/utils/DateFormatter/index";
type EventsTabProps = LiveStatusObjectDrawerTabProps & {
    projectId: string;
    environmentId: string;
    tenantId: string | undefined;
};
const styles = {
    header: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
        paddingLeft: space[16],
        paddingRight: space[16],
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    filterItemContainer: css({
        width: `calc(15 * ${space[16]})`,
    }),
    headerActions: css({
        marginLeft: "auto",
        display: "flex",
        flexDirection: "row",
        gap: space[16],
    }),
    headerText: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    eventsTableContainer: css({
        height: "100%",
        overflowY: "auto",
    }),
    logsTable: css({
        width: "100%",
        borderSpacing: 0,
        borderCollapse: "collapse",
        tableLayout: "fixed",
    }),
    logsTableTh: css({
        //we use content-box here so we can more easily specify fixed column widths (without having to calculate padding in the width)
        boxSizing: "content-box",
        textTransform: "uppercase",
        font: text.heading.xSmall,
        color: themeTokens.color.text.secondary,
        textAlign: "left",
        padding: `${space[8]} ${space[12]}`,
        whiteSpace: "nowrap",
        ":first-of-type": {
            paddingLeft: space[16],
        },
        ":last-of-type": {
            paddingRight: space[16],
        },
        backgroundColor: themeTokens.color.background.primary.default,
        position: "sticky",
        top: 0,
    }),
    //We define fixed widths for all the <th>'s
    typeTh: css({
        width: space[32],
    }),
    lastSeenTh: css({
        width: space[64],
    }),
    reasonTh: css({
        width: `calc(10 * ${space[16]})`,
    }),
    countTh: css({
        width: space[48],
    }),
    chevronTh: css({
        width: space[16],
    }),
    td: css({
        padding: `${space[8]} ${space[12]}`,
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        ":first-of-type": {
            paddingLeft: space[16],
        },
        ":last-of-type": {
            paddingRight: space[16],
        },
    }),
    centeredTd: css({
        textAlign: "center",
    }),
    eventTr: css({
        ":hover": {
            backgroundColor: themeTokens.color.background.primary.hover,
        },
    }),
    eventTd: css({
        fontFamily: fontFamilyGlobal["system-ui-monospace"],
        whiteSpace: "nowrap",
    }),
    expandedEventTr: css({
        borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    wrappingTd: css({
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    }),
    typeIconContainer: css({
        //this is because of a strange interaction with the tooltip and it affecting the row height
        display: "flex",
        justifyContent: "center",
    }),
    expansionTr: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        borderBottom: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
    expansionTd: css({
        paddingTop: space[4],
        paddingBottom: space[4],
        whiteSpace: "normal",
    }),
    expansionDetail: css({
        paddingTop: space[4],
        paddingBottom: space[4],
        display: "flex",
        flexDirection: "row",
        gap: space[16],
    }),
    expansionLabel: css({
        width: `calc(6 * ${space[16]})`,
    }),
    loadingTd: css({
        height: `calc(20px + (${space[8]} * 2))`,
    }),
};
type Filters = {
    search?: string;
    type?: KubernetesEventType;
};
function SkeletonTd() {
    return (<td className={cx(styles.td, styles.eventTd, styles.loadingTd)}>
            <Skeleton shape={"Rounded"} borderRadius={"small"}/>
        </td>);
}
export const EventsTab = (props: EventsTabProps) => {
    const { detailedResource, projectId, environmentId, tenantId } = props;
    const [filters, setFilters] = useState<Filters>({});
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const { result, isLoading, error, refetch, requestCompletedTime } = useEventsQuery(detailedResource, projectId, environmentId, tenantId);
    const filteredEvents = useMemo(() => {
        if (!filters.search && !filters.type) {
            return result?.Events;
        }
        let events = result?.Events;
        const lowerCaseSearch = filters?.search?.toLocaleLowerCase();
        if (lowerCaseSearch) {
            events = events?.filter((e) => e.Reason.toLocaleLowerCase().includes(lowerCaseSearch) || e.Note.toLocaleLowerCase().includes(lowerCaseSearch));
        }
        if (filters.type) {
            events = events?.filter((e) => e.Type === filters.type);
        }
        return events;
    }, [filters.search, filters.type, result?.Events]);
    const toggleExpandedRow = (id: string) => setExpandedRows((prev) => {
        //clone the set
        const updated = new Set(prev);
        if (updated.has(id)) {
            updated.delete(id);
        }
        else {
            updated.add(id);
        }
        return updated;
    });
    const formatLastSeen = (lastSeen: string): string => {
        const duration = moment.duration(requestCompletedTime.diff(lastSeen));
        const secs = duration.seconds();
        const mins = duration.minutes();
        const hours = duration.hours();
        const days = Math.floor(duration.asDays());
        let result = secs > 0 ? `${secs}s` : "";
        result = `${mins > 0 ? `${mins}m` : ""}${result}`;
        result = `${hours > 0 ? `${hours}h` : ""}${result}`;
        result = `${days > 0 ? `${days}d` : ""}${result}`;
        return result;
    };
    const updateFilters = (partial: Partial<Filters>) => setFilters((prev) => ({ ...prev, ...partial }));
    const errorInfo = useMemo(() => createErrorInfo(error) || createErrorInfoFromMonitorError(result?.Error), [error, result?.Error]);
    if (errorInfo) {
        return <ErrorPanel error={errorInfo}/>;
    }
    return (<>
            <div className={styles.header}>
                <div className={styles.filterItemContainer}>
                    <SearchInput fullWidth={true} value={filters.search || ""} onChange={(val) => updateFilters({ search: val })} accessibleName={"Search pod logs"} placeholder={"Search"}/>
                </div>
                <div className={styles.filterItemContainer}>
                    <DeprecatedSelect label={"Filter by type"} value={filters.type} onChange={(val) => updateFilters({ type: val })} items={AllKubernetesEventTypes.map((val) => ({ value: val, text: val }))} allowClear={true}/>
                </div>
                <div className={styles.headerActions}>
                    <span className={styles.headerText}>
                        Last refreshed <TimeFromNowLabel time={requestCompletedTime}/>
                    </span>
                    <Tooltip content={"Refresh"}>
                        <DeprecatedIconButton customIcon={<ArrowRefreshIcon size={20}/>} onClick={refetch}/>
                    </Tooltip>
                </div>
            </div>
            <div className={styles.eventsTableContainer}>
                <table className={styles.logsTable}>
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    {(isLoading || (filteredEvents && filteredEvents.length > 0)) && (<thead>
                            <tr>
                                <th className={cx(styles.logsTableTh, styles.typeTh)}>Type</th>
                                <th className={cx(styles.logsTableTh, styles.lastSeenTh)}>Last Seen</th>
                                <th className={cx(styles.logsTableTh, styles.reasonTh)}>Reason</th>
                                <th className={cx(styles.logsTableTh)}>Message</th>
                                <th className={cx(styles.logsTableTh, styles.countTh)}>Count</th>
                                <th className={cx(styles.logsTableTh, styles.chevronTh)}></th>
                            </tr>
                        </thead>)}
                    <tbody>
                        {isLoading &&
            Array.from({ length: 5 }, (_, i) => (<tr key={`loadingSkeleton-${i}`}>
                                    <SkeletonTd />
                                    <SkeletonTd />
                                    <SkeletonTd />
                                    <SkeletonTd />
                                    <SkeletonTd />
                                    <td className={cx(styles.td, styles.eventTd, styles.loadingTd)}>&nbsp;</td>
                                </tr>))}
                        {!isLoading &&
            filteredEvents &&
            (filteredEvents.length > 0 ? (<>
                                    {filteredEvents.map((ev, i) => {
                    //we just need a unique id for this event row, so this will do
                    const eventId = `${i}-${ev.Reason}-${ev.FirstObservedTime}`;
                    const isExpanded = expandedRows.has(eventId);
                    return (<Fragment key={eventId}>
                                                <tr key={eventId} className={cx(styles.eventTr, isExpanded ? styles.expandedEventTr : undefined)}>
                                                    <td className={cx(styles.td, styles.eventTd, styles.centeredTd)}>
                                                        <div className={styles.typeIconContainer}>
                                                            <Tooltip content={ev.Type}>{ev.Type === "Normal" ? <InfoCircleIcon size={20}/> : <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>}</Tooltip>
                                                        </div>
                                                    </td>
                                                    <td className={cx(styles.td, styles.eventTd)}>
                                                        <span>{formatLastSeen(ev.LastObservedTime)}</span>
                                                    </td>
                                                    <td className={cx(styles.td, styles.eventTd, styles.wrappingTd)} colSpan={isExpanded ? 2 : 1}>
                                                        <span>{ev.Reason}</span>
                                                    </td>
                                                    {!isExpanded && (<td className={cx(styles.td, styles.eventTd, styles.wrappingTd)}>
                                                            <span>{ev.Note}</span>
                                                        </td>)}
                                                    <td className={cx(styles.td, styles.eventTd)}>
                                                        <span>{ev.Count}</span>
                                                    </td>
                                                    <td className={cx(styles.td, styles.eventTd)}>
                                                        <DeprecatedIconButton customIcon={isExpanded ? <ChevronDownIcon size={16} color={themeTokens.color.icon.primary}/> : <ChevronRightIcon size={16} color={themeTokens.color.icon.primary}/>} onClick={() => toggleExpandedRow(eventId)}/>
                                                    </td>
                                                </tr>
                                                {isExpanded && (<tr key={`${eventId}-details`} className={styles.expansionTr}>
                                                        <td colSpan={6} className={cx(styles.td, styles.eventTd, styles.expansionTd)}>
                                                            <div className={styles.expansionDetail}>{ev.Note}</div>
                                                            <div className={styles.expansionDetail}>
                                                                <div className={styles.expansionLabel}>First seen:</div>
                                                                <div>{DateFormatter.dateToShortFormat(ev.FirstObservedTime)}</div>
                                                            </div>
                                                            <div className={styles.expansionDetail}>
                                                                <div className={styles.expansionLabel}>Last seen:</div>
                                                                <div>{DateFormatter.dateToShortFormat(ev.LastObservedTime)}</div>
                                                            </div>
                                                        </td>
                                                    </tr>)}
                                            </Fragment>);
                })}
                                </>) : (<tr>
                                    <td colSpan={6}>
                                        <EmptyStateNoResults />
                                    </td>
                                </tr>))}
                    </tbody>
                </table>
            </div>
        </>);
};
